import React from "react";
import { MapPin, Mail, Phone, Instagram, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function Contact({ settings }) {
  const address =
    settings?.location || "Ibiza Poolside Villa Resort, Palo, Leyte, Philippines";
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    address
  )}`;
  const [copied, setCopied] = React.useState(false);
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success("Address copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-16 md:py-32 px-5 sm:px-6 md:px-12 lg:px-24 max-w-7xl mx-auto"
    >
      <div className="grid md:grid-cols-12 gap-8 md:gap-12">
        <div className="md:col-span-5">
          <p className="overline mb-3 md:mb-4">Get in Touch</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            Plan Your <span className="italic text-[#C5A059]">Escape</span>
          </h2>
          <div className="gold-divider mt-6 md:mt-8" />
          <p className="text-[#A1A4B5] mt-6 md:mt-8 leading-relaxed text-sm sm:text-base">
            Our concierge team is available to assist with bespoke arrangements,
            event hosting, and curated experiences during your stay.
          </p>
        </div>

        <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)] rounded-2xl overflow-hidden">
          <div className="bg-[#0A0B10] p-6 sm:p-8">
            <MapPin className="text-[#C5A059]" size={22} />
            <h4 className="font-serif-display text-lg sm:text-xl mt-3 sm:mt-4">Location</h4>
            <p className="text-[#A1A4B5] text-sm mt-2">
              {settings?.location || "Tagaytay, Philippines"}
            </p>
          </div>
          <div className="bg-[#0A0B10] p-6 sm:p-8">
            <Mail className="text-[#C5A059]" size={22} />
            <h4 className="font-serif-display text-lg sm:text-xl mt-3 sm:mt-4">Email</h4>
            <a
              href={`mailto:${settings?.contact_email}`}
              className="luxury-link text-sm mt-2 inline-block break-all"
              data-testid="contact-email-link"
            >
              {settings?.contact_email || "stay@ibizapoolside.com"}
            </a>
          </div>
          <div className="bg-[#0A0B10] p-6 sm:p-8">
            <Phone className="text-[#C5A059]" size={22} />
            <h4 className="font-serif-display text-lg sm:text-xl mt-3 sm:mt-4">Phone</h4>
            <a
              href={`tel:${settings?.contact_phone}`}
              className="luxury-link text-sm mt-2 inline-block"
              data-testid="contact-phone-link"
            >
              {settings?.contact_phone || "+63 917 000 0000"}
            </a>
          </div>
          <div className="bg-[#0A0B10] p-6 sm:p-8">
            <Instagram className="text-[#C5A059]" size={22} />
            <h4 className="font-serif-display text-lg sm:text-xl mt-3 sm:mt-4">Instagram</h4>
            <p className="text-[#A1A4B5] text-sm mt-2">
              {settings?.instagram || "@ibizapoolside"}
            </p>
          </div>
        </div>
      </div>

      {/* Map pin */}
      <div className="mt-10 md:mt-14" data-testid="contact-map">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
          <div>
            <p className="overline mb-2">Find Us</p>
            <h3 className="font-serif-display text-2xl sm:text-3xl md:text-4xl tracking-tight">
              Pinned on the <span className="italic text-[#C5A059]">Map</span>
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={copyAddress}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#C5A059]/40 bg-[#13151D] px-4 py-2 text-xs sm:text-sm text-[#C5A059] hover:bg-[#C5A059]/10 transition-colors"
              data-testid="contact-copy-address"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy Address"}
            </button>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#C5A059] px-4 py-2 text-xs sm:text-sm font-medium text-[#0A0B10] hover:bg-[#d4b270] transition-colors"
              data-testid="contact-open-google-maps"
            >
              <MapPin size={14} />
              Open in Google Maps
            </a>
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden border border-[var(--border)] bg-[#0A0B10] relative">
          {/* Gold accent corners */}
          <div className="pointer-events-none absolute inset-0 ring-1 ring-[#C5A059]/20 rounded-2xl z-10" />
          <iframe
            title="Ibiza Poolside Villa Resort — Map"
            src={`https://www.google.com/maps?q=${encodeURIComponent(
              address
            )}&output=embed&z=15`}
            width="100%"
            height="420"
            style={{ border: 0, filter: "grayscale(0.3) contrast(1.05)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="contact-map-iframe"
          />
        </div>
      </div>
    </section>
  );
}
